import clsx from "clsx";
import { Layers, Plus } from "lucide-react";
import { useState } from "react";
import { ErrorNotice, ListWarning, Notice, PageHeader, Panel } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { rowOpen } from "../../web/lib/rowOpen";
import { basisLabel, type MoneyFormatter } from "../../shared/money";
import { can, freeGroupName, groupTaxBasis, membersOf, outOfDate, usePriceListSaver, whyNot } from "./data";
import { describeGroup } from "./describe";
import { ANY_KIND, FilterBar, matchesSearch } from "./ListToolbar";
import { CheckedListMark, type CheckedFilter, CheckedOnly, inCheckedFilter } from "./CheckedMark";
import { type CheckedMark, type PriceListData, type GroupRow, GROUP_KIND_LABEL, groupKind } from "./meta";
import { attentionByGroup, attentionText } from "./attention";

/*
 * Pricing groups, every kind in one list (kept alike: groups.ts): a fixed price (the same price for all its items), a markup
 * on each item's cost, or a markup tiered by cost (as ServiceM8 has them). Each opens in its own window (details, pricing, items,
 * stats); a new one is named "New ..." for now, its window opening with the name ready to type over. Those whose items' prices,
 * costs or names need updating are flagged (they're updated from its window). Tags group items otherwise.
 */

/** The kinds of what needs attention on a group (FilterBar's chips): its products' prices or costs to update, no cost, or anything else. */
type Kind = "prices" | "costs" | "noCost" | "attention";


export function GroupsList(props: Readonly<{ data: PriceListData; openId?: string; onOpen: (id: string, options?: { rename?: boolean }) => void }>) {
    const { data } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    // A link can open it already searched (e.g. "See all" from Home's search): ?q=...
    const [query, setQuery] = useState(() => new URLSearchParams(globalThis.location.search).get("q") ?? "");
    // A link (the sidebar's count) can open it narrowed to what needs attention: ?filter=attention.
    const [kind, setKind] = useState<Kind | typeof ANY_KIND | null>(() => (new URLSearchParams(globalThis.location.search).get("filter") === "attention" ? ANY_KIND : null));
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [checkedOnly, setCheckedOnly] = useState<CheckedFilter>("");
    // Its items needing attention (the same checks as the Materials list), every group's at once. Needs attention, all of it, is
    // groupsNeedingAttention's (what the sidebar counts beside Groups).
    const attention = attentionByGroup(data);
    const groups = [...data.groups]
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((g) => inCheckedFilter(checkedOnly, data, g.id))
        .map((g) => ({ g, items: membersOf(data, g.id).length, stale: outOfDate(data, g), attention: attention.get(g.id) ?? null }));
    type Row = (typeof groups)[number];
    const hasKind = (k: Kind | typeof ANY_KIND, row: Row) => {
        if (k === "attention") return row.attention !== null;
        if (k === ANY_KIND) return row.stale.prices + row.stale.costs + row.stale.noCost > 0 || row.attention !== null;
        return row.stale[k] > 0;
    };
    const kindOf = (id: Kind, label: string, danger = false) => ({ id, label, count: groups.filter((row) => hasKind(id, row)).length, danger });
    const kinds = [
        kindOf("attention", "Products needing attention", groups.some((row) => row.attention?.danger)),
        kindOf("prices", "Prices to update"),
        kindOf("costs", "Costs to update"),
        kindOf("noCost", "Items with no cost"),
    ];
    const rows = groups.filter((row) => (kind === null || hasKind(kind, row)) && matchesSearch(`${row.g.name} ${row.g.description}`, query));
    // A new one of a kind: a fixed price at $0; a markup of 0% on every cost; a tiered one, two tiers to fill in.
    // A new one: a fixed price at $0 to start, its window opening to name it and choose its kind.
    const newGroup = () => {
        setCreating(true);
        setError(null);
        saver
            .createGroup({ name: freeGroupName(data, "New group"), basePrice: "0" })
            .then((s) => props.onOpen(s.id, { rename: true }))
            .catch((err: Error) => setError(err))
            .finally(() => setCreating(false));
    };

    // A group's pricing (cost plus a markup) gives its items' costs away: only with See costs.
    if (!can(data, "costs.view")) {
        return (
            <div className="page is-wide">
                <PageHeader title="Groups" icon={<Layers className="size-6" />} tone="violet" subtitle="Products priced alike: change one once, for all its products. An exception can be priced its own way." />
                <Notice>{whyNot(data, "costs.view")}</Notice>
            </div>
        );
    }

    return (
        <div className="page is-wide">
            <PageHeader title="Groups" icon={<Layers className="size-6" />} tone="violet" subtitle="Products priced alike: change one once, for all its products. An exception can be priced its own way." />
            {error ? (
                <div className="mb-4">
                    <ErrorNotice error={error} />
                </div>
            ) : null}
            <Panel
                flush
                title="Groups"
                actions={
                    can(data, "groups") ? (
                        <>
                            <button type="button" className="btn-secondary" disabled={creating} title="A fixed price, a markup or a tiered markup: chosen in its window" onClick={newGroup}>
                                <Plus className="size-4" /> New group
                            </button>
                        </>
                    ) : null
                }
            >
                <FilterBar
                    searchLabel="Search groups"
                    placeholder="Search by name"
                    query={query}
                    onQuery={setQuery}
                    kinds={kinds}
                    anyCount={groups.filter((row) => hasKind(ANY_KIND, row)).length}
                    kind={kind}
                    onKind={setKind}
                >
                    <CheckedOnly value={checkedOnly} onChange={setCheckedOnly} />
                </FilterBar>
                <table className="list is-fixed min-w-[560px]">
                    <colgroup>
                        <col className="w-[24%]" />
                        <col className="w-[14%]" />
                        <col className="w-[26%]" />
                        <col className="w-[36%]" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Kind</th>
                            <th>Pricing</th>
                            <th className="text-right">Products</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ g, items, stale }) => (
                            <GroupTimeRow
                                key={g.id}
                                group={g}
                                checked={data.checked[g.id]}
                                pricing={groupPricing(props.data, g, fmt)}
                                items={items}
                                stale={stale}
                                attention={attention.get(g.id) ?? null}
                                open={props.openId === g.id}
                                onOpen={() => props.onOpen(g.id)}
                            />
                        ))}
                    </tbody>
                </table>
                {rows.length === 0 ? <p className="p-6 text-center text-sm text-muted">{data.groups.length ? "None match." : "None yet."}</p> : null}
            </Panel>
        </div>
    );
}

/** A group's pricing in a few words: a fixed price's amounts as it taxes its items ("Fixed $249.00 · inc GST"), a markup's %. */
function groupPricing(data: PriceListData, g: GroupRow, fmt: MoneyFormatter) {
    const taxed = groupTaxBasis(data, g.tax);
    return describeGroup(g, taxed, basisLabel(taxed.includesTax, taxed.rate), fmt);
}

/** A group: its name, its pricing, and how many items (with what of theirs doesn't match it yet). */
function GroupTimeRow(
    props: Readonly<{
        group: GroupRow;
        checked: CheckedMark | undefined;
        pricing: string;
        items: number;
        stale: ReturnType<typeof outOfDate>;
        /** Its items needing attention (attention.ts), or null: none. */
        attention: { count: number; danger: boolean } | null;
        open: boolean;
        onOpen: () => void;
    }>,
) {
    const { group, stale, attention } = props;
    // What of its products doesn't match it yet, or is to update ("2 prices", "1 cost", "1 no cost").
    const outOfStep = [
        stale.prices ? `${stale.prices} ${stale.prices === 1 ? "price" : "prices"}` : "",
        stale.costs ? `${stale.costs} ${stale.costs === 1 ? "cost" : "costs"}` : "",
        stale.noCost ? `${stale.noCost} no cost` : "",
    ].filter(Boolean);
    return (
        // The name button covers the whole row (after:inset-0): click selects it, double-click opens the group.
        <tr className={clsx("relative", attention?.danger ? "is-danger" : (outOfStep.length > 0 || attention) && "is-warning", props.open ? "bg-brand-500/10" : "hover:bg-surface-2 focus-within:bg-brand-500/5")}>
            <td className="h-14">
                <button type="button" className="block w-full min-w-0 cursor-default text-left outline-none after:absolute after:inset-0" {...rowOpen(props.onOpen)}>
                    <span className="flex min-w-0 items-center gap-1.5">
                        <CheckedListMark mark={props.checked} />
                        <span className="truncate font-semibold">{group.name}</span>
                    </span>
                </button>
            </td>
            <td className="text-muted">{GROUP_KIND_LABEL[groupKind(group)]}</td>
            <td className="truncate text-muted">{props.pricing}</td>
            <td className="text-right">
                <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 py-1">
                    {attention ? (
                        <ListWarning tone={attention.danger ? "danger" : "warning"} title={`${attentionText(attention.count)}: open it to see them.`}>
                            {attentionText(attention.count)}
                        </ListWarning>
                    ) : null}
                    {outOfStep.length > 0 ? (
                        <ListWarning title="Items that don't match it yet (prices, names), costs to update, or no cost to work a price out from. Open it to update them.">
                            {outOfStep.join(" · ")}
                        </ListWarning>
                    ) : null}
                    <span className="tabular-nums">{props.items.toLocaleString()}</span>
                </span>
            </td>
        </tr>
    );
}
